import React, { useState } from "react";
import {
  EditorState,
  ContentState,
  convertToRaw,
  convertFromHTML,
} from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import { Box, Typography, Paper, Button } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import draftToHtml from "draftjs-to-html"; // To convert Draft.js content to HTML
import apiService from "../../service/Api";
import { showErrorToast, showSuccessToast } from "./ShowToast";

interface ObituaryEditorProps {
  obituary: string; // Optional string prop with default ''
  deceasedId?: number; // Required number prop
}

const ObituaryEditor: React.FC<ObituaryEditorProps> = ({
  obituary,
  deceasedId,
}) => {
  const theme = useSelector((state: RootState) => state.theme);
  // Initialize EditorState with the provided obituary (convert HTML to Draft.js content)
  const [editorState, setEditorState] = useState(() => {
    try {
      const blocksFromHTML = convertFromHTML(obituary);
      const contentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap
      );
      return EditorState.createWithContent(contentState);
    } catch (error) {
      console.error("Error initializing editor state:", error);
      // Fallback to empty editor state if conversion fails
      return EditorState.createEmpty();
    }
  });

  const toolbar = {
    options: [
      "inline",
      "blockType",
      "list",
      "textAlign",
      "link",
      "image",
      "remove",
      "history",
    ],
    inline: {
      options: ["bold", "italic", "underline", "strikethrough"],
    },
    blockType: {
      options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6", "Blockquote"],
    },
    list: {
      options: ["unordered", "ordered"],
    },
    textAlign: {
      options: ["left", "center", "right", "justify"],
    },
  };

  const handleEditorChange = (newEditorState: EditorState) => {
    setEditorState(newEditorState);
  };

  const handleSaveClick = async () => {
    try {
      // Convert Draft.js content to HTML
      const rawContent = convertToRaw(editorState.getCurrentContent());
      const htmlContent = draftToHtml(rawContent);
      await apiService.patch(`/api/individuals/deceased/${deceasedId}/`, {
        orbituary: htmlContent,
      });
      showSuccessToast("Obituary updated successfully");
    } catch (error) {
      // Handle error gracefully
      const errorMessage =
        error instanceof Error ? error.message : "An unexpected error occurred";
      showErrorToast(`Error updating obituary: ${errorMessage}`);
    }
  };

  return (
    <Paper
      sx={{ p: 3, backgroundColor: theme.primary_colors[800], boxShadow: 0 }}
    >
      {" "}
      {/* Light background (#f0f4f8) for better visibility */}
      <Typography variant="h5" sx={{ mb: 2, color: theme.primary_colors[700] }}>
        {" "}
        {/* Black text (#000000) for high contrast */}
        Write Obituary
      </Typography>
      <Box
        sx={{
          ".rdw-editor-toolbar": {
            backgroundColor:
              theme
                .primary_colors[900] /* White background (#ffffff) for toolbar */,
            borderColor: theme.primary_colors[50] /* Brown border (#C29363) */,
            color: theme.primary_colors[700] /* Black text (#000000) */,
          },
          ".rdw-option-wrapper, .rdw-dropdown-wrapper": {
            backgroundColor:
              theme.primary_colors[900] /* White background (#ffffff) */,
            color: theme.primary_colors[700] /* Black text (#000000) */,
            borderColor: theme.primary_colors[50] /* Brown border (#C29363) */,
          },
          ".rdw-option-wrapper:hover, .rdw-option-active": {
            backgroundColor:
              theme.primary_colors[600] /* Light brown hover (#E5BFA5) */,
          },
          ".rdw-editor-main": {
            minHeight: "200px",
            backgroundColor:
              theme
                .primary_colors[900] /* White background (#ffffff) for editor */,
            color:
              theme
                .primary_colors[700] /* Black text (#000000) for high contrast */,
            border: `1px solid ${theme.primary_colors[300]}` /* Brown border (#C29363) */,
            padding: "8px",
          },
          ".DraftEditor-root": {
            color: theme.primary_colors[700] /* Black text (#000000) */,
            "& p": { color: theme.primary_colors[700] } /* Black paragraphs */,
            "& h1, & h2, & h3, & h4, & h5, & h6": {
              color: theme.primary_colors[700],
            } /* Black headings */,
            "& a": {
              color: theme.primary_colors[10],
            } /* Dark brown links (#a36e45) */,
            lineHeight: 1.7,
            fontSize: { xs: "0.875rem", md: "1rem" },
          },
        }}
      >
        <Editor
          editorState={editorState}
          onEditorStateChange={handleEditorChange}
          toolbar={toolbar}
          placeholder="Start writing the obituary here..."
        />
      </Box>
      <Button
        variant="contained"
        sx={{
          mt: 3,
          backgroundColor: theme.primary_colors[500],
          "&:hover": { backgroundColor: theme.primary_colors[400] },
        }}
        onClick={handleSaveClick}
      >
        Save Obituary
      </Button>
    </Paper>
  );
};

export default ObituaryEditor;
